import { ArrowLeft, ChevronRight } from "lucide-react";
import type { SupportFormField } from "@gaole/shared";
import { Button } from "../ui";
import { SupportFormFieldInput } from "./SupportFormFieldInput";

export interface WizardFormDto {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  fields: SupportFormField[];
  subCategoryId: string | null;
}

export interface WizardCategory {
  id: string;
  name: string;
  subCategories: { id: string; name: string; formCount: number }[];
}

export type WizardPhase = "category" | "subcategory" | "pickForm" | "fillForm";

interface SupportRequestWizardProps {
  phase: WizardPhase;
  categories: WizardCategory[];
  otherFormCount: number;
  selectedCategoryId: string | null;
  selectedSubCategoryId: string | null;
  formsInSub: WizardFormDto[];
  activeForm: WizardFormDto | null;
  formValues: Record<string, string>;
  busy: boolean;
  onClose: () => void;
  onBack: () => void;
  onSelectCategory: (categoryId: string) => void;
  onSelectOther: () => void;
  onSelectSubCategory: (subCategoryId: string) => void;
  onSelectForm: (form: WizardFormDto) => void;
  onFormValueChange: (key: string, value: string) => void;
  onSubmit: () => void;
}

export function SupportRequestWizard({
  phase,
  categories,
  otherFormCount,
  selectedCategoryId,
  selectedSubCategoryId,
  formsInSub,
  activeForm,
  formValues,
  busy,
  onClose,
  onBack,
  onSelectCategory,
  onSelectOther,
  onSelectSubCategory,
  onSelectForm,
  onFormValueChange,
  onSubmit,
}: SupportRequestWizardProps) {
  const selectedCategory = categories.find((c) => c.id === selectedCategoryId);
  const selectedSub = selectedCategory?.subCategories.find((s) => s.id === selectedSubCategoryId);

  return (
    <section className="space-y-3 rounded-2xl border border-brand-pink/30 bg-surface p-4">
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          {phase !== "category" && (
            <button type="button" onClick={onBack} className="shrink-0 text-white/50 hover:text-white" aria-label="Back">
              <ArrowLeft size={18} />
            </button>
          )}
          <div className="min-w-0">
            <p className="font-display text-sm font-bold">
              {phase === "category" && "Choose category"}
              {phase === "subcategory" && selectedCategory?.name}
              {phase === "pickForm" && `${selectedCategory?.name} · ${selectedSub?.name}`}
              {phase === "fillForm" && activeForm?.name}
            </p>
            <p className="text-[10px] text-white/40">
              {phase === "category" && "Step 1 of 3"}
              {phase === "subcategory" && "Step 2 of 3 — sub-category"}
              {phase === "pickForm" && "Choose request type"}
              {phase === "fillForm" && "Complete the form"}
            </p>
          </div>
        </div>
        <button type="button" onClick={onClose} className="shrink-0 text-xs text-white/40 hover:text-white">
          Cancel
        </button>
      </div>

      {phase === "category" && (
        <ul className="space-y-2">
          {categories.map((cat) => (
            <li key={cat.id}>
              <button
                type="button"
                onClick={() => onSelectCategory(cat.id)}
                className="flex w-full items-center justify-between rounded-xl border border-white/10 bg-white/[0.03] px-3 py-3 text-left active:border-brand-pink/40"
              >
                <span className="text-xs font-semibold">{cat.name}</span>
                <ChevronRight size={16} className="text-white/30" />
              </button>
            </li>
          ))}
          {otherFormCount > 0 && (
            <li>
              <button
                type="button"
                onClick={onSelectOther}
                className="flex w-full items-center justify-between rounded-xl border border-white/10 bg-white/[0.03] px-3 py-3 text-left active:border-brand-pink/40"
              >
                <span className="text-xs font-semibold">Other</span>
                <ChevronRight size={16} className="text-white/30" />
              </button>
            </li>
          )}
        </ul>
      )}

      {phase === "subcategory" && selectedCategory && (
        <ul className="space-y-2">
          {selectedCategory.subCategories.map((sub) => (
            <li key={sub.id}>
              <button
                type="button"
                onClick={() => onSelectSubCategory(sub.id)}
                className="flex w-full items-center justify-between rounded-xl border border-white/10 bg-white/[0.03] px-3 py-3 text-left active:border-brand-pink/40"
              >
                <div>
                  <span className="text-xs font-semibold">{sub.name}</span>
                  <p className="text-[10px] text-white/40">
                    {sub.formCount} request{sub.formCount === 1 ? "" : "s"}
                  </p>
                </div>
                <ChevronRight size={16} className="text-white/30" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {phase === "pickForm" && (
        <ul className="space-y-2">
          {formsInSub.map((form) => (
            <li key={form.id}>
              <button
                type="button"
                onClick={() => onSelectForm(form)}
                className="flex w-full flex-col rounded-xl border border-white/10 bg-white/[0.03] px-3 py-3 text-left active:border-brand-pink/40"
              >
                <span className="text-xs font-semibold">{form.name}</span>
                {form.description && (
                  <span className="mt-0.5 line-clamp-2 text-[10px] text-white/45">{form.description}</span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}

      {phase === "fillForm" && activeForm && (
        <div className="space-y-2.5">
          {activeForm.description && (
            <p className="text-[11px] text-white/50">{activeForm.description}</p>
          )}
          {activeForm.fields.map((field) => (
            <div key={field.key}>
              <label className="mb-1 block text-[11px] text-white/60">
                {field.label}
                {field.required && <span className="text-brand-pink"> *</span>}
              </label>
              <SupportFormFieldInput
                field={field}
                value={formValues[field.key] ?? ""}
                onChange={(v) => onFormValueChange(field.key, v)}
              />
            </div>
          ))}
          <Button className="w-full" disabled={busy} onClick={onSubmit}>
            {busy ? "Submitting…" : "Submit request"}
          </Button>
        </div>
      )}
    </section>
  );
}
